import {
  ActionButtons,
  DataTable,
  type DataTableColumn,
  type DataTableRow,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { useNoteEditorOverlay } from '@components/common/NoteEditorOverlay'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import { PatientWithInsurance } from '@dataTypes/appointment.type'
import { AmountType, PaymentEntry } from '@dataTypes/billing.types'
import { NoteContextType } from '@dataTypes/note.type'
import { Patient } from '@dataTypes/PatientInfo.types'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useAppSelector } from '@store/hooks'
import { formatCurrency, formatInvoiceNumber } from '@utils/billing.utils'
import { createFailedToast } from '@utils/notificationToast.util'
import React, { useCallback, useMemo, useState } from 'react'
import './PaymentTableStyle.css'

interface Props {
  filteredHistory: PaymentEntry[]
  paymentHistoryCount: number
  currentPage: number
  pageSize: number
  handlePageChange: (page: number) => void
  handlePageSizeChange: (pageSize: number) => void
  handlePaymentHistoryTableSortChange: (sort: {
    key: string
    direction: SortDirection
  }) => void
  allPatients: PatientWithInsurance[]
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const PaymentHistoryTable: React.FC<Props> = ({
  filteredHistory,
  paymentHistoryCount,
  currentPage,
  pageSize,
  handlePageChange,
  handlePageSizeChange,
  handlePaymentHistoryTableSortChange,
  allPatients,
}) => {
  const { openNoteEditor } = useNoteEditorOverlay()
  const { showToast } = useNotificationToast()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const [sortColumnId, setSortColumnId] = useState<string | undefined>(
    undefined,
  )
  const [sortDirection, setSortDirection] = useState<
    DataTableSortDirection | undefined
  >(undefined)

  const resolvePaymentPatient = useCallback(
    (row: PaymentEntry): Patient | null => {
      if (selectedPatient) {
        return selectedPatient
      }

      const paidByName = row.paidBy.trim().toLowerCase()
      const matchedPatient = allPatients.find((patient) => {
        const fullName =
          `${patient.first_name || ''} ${patient.last_name || ''}`
            .trim()
            .toLowerCase()
        return fullName === paidByName
      })

      if (!matchedPatient) {
        return null
      }

      return matchedPatient as unknown as Patient
    },
    [allPatients, selectedPatient],
  )

  const historyById = useMemo(() => {
    const entries = new Map<string, PaymentEntry>()
    filteredHistory.forEach((entry) => {
      entries.set(entry.paymentId, entry)
    })
    return entries
  }, [filteredHistory])

  const rows = useMemo(
    (): DataTableRow[] =>
      filteredHistory.map((entry) => {
        const isFullPayment = entry.amountPaid >= entry.amountDue
        return {
          id: entry.paymentId,
          invoiceId: formatInvoiceNumber(entry.invoiceId),
          paymentDate: entry.paymentDate,
          amountDue: formatCurrency(entry.amountDue),
          method: `${entry.method} - ${
            isFullPayment ? AmountType.Full : AmountType.Partial
          }`,
          amountPaid: formatCurrency(entry.amountPaid),
          paidBy: entry.paidBy,
        }
      }),
    [filteredHistory],
  )

  const openPaymentNotes = useCallback(
    (entry: PaymentEntry): void => {
      const overlayPatient = resolvePaymentPatient(entry)
      if (!overlayPatient) {
        showToast(
          createFailedToast(
            'Cannot open notes',
            'Patient context is unavailable for this payment row.',
          ),
        )
        return
      }

      openNoteEditor({
        patient: overlayPatient,
        contextType: NoteContextType.BILLING,
        contextId: entry.paymentId,
        openFirstNote: true,
        initialView: 'list',
        hideNotesTable: true,
        overlayBackLabel: 'Back to billing',
        editorBackButtonLabel: 'Back to billing',
        openContextNotesByDefault: true,
        notesFetchLoadingContainer: 'body',
        portalTarget: '.page-content',
      })
    },
    [openNoteEditor, resolvePaymentPatient, showToast],
  )

  const columns = useMemo(
    (): DataTableColumn[] => [
      { id: 'invoiceId', label: 'Invoice no.', width: '150px' },
      {
        id: 'paymentDate',
        label: 'Payment date',
        sortable: true,
        width: '150px',
      },
      { id: 'amountDue', label: 'Amount due', width: '150px' },
      { id: 'method', label: 'Method', sortable: true, width: '150px' },
      { id: 'amountPaid', label: 'Amount paid', width: '150px' },
      { id: 'paidBy', label: 'Paid by', sortable: true, width: '150px' },
    ],
    [],
  )

  const handleSort = useCallback(
    (columnId: string): void => {
      const nextDirection: DataTableSortDirection =
        sortColumnId === columnId && sortDirection === 'asc' ? 'desc' : 'asc'
      setSortColumnId(columnId)
      setSortDirection(nextDirection)
      handlePaymentHistoryTableSortChange({
        key: columnId,
        direction:
          nextDirection === 'asc' ? SortDirection.ASC : SortDirection.DESC,
      })
    },
    [handlePaymentHistoryTableSortChange, sortColumnId, sortDirection],
  )

  const renderActions = (row: DataTableRow): React.ReactNode => {
    const entry = historyById.get(row.id)
    const hasReferenceNumber =
      typeof entry?.referenceNumber === 'string' &&
      entry.referenceNumber.trim().length > 0
    const canViewNote = Boolean(hasReferenceNumber && entry?.paymentId)

    return (
      <ActionButtons
        onViewNotes={() => {
          if (entry) openPaymentNotes(entry)
        }}
        disableViewNotes={!canViewNote}
      />
    )
  }

  const totalPages = Math.max(1, Math.ceil(paymentHistoryCount / pageSize))

  return (
    <DataTable
      className="payment-history-table"
      columns={columns}
      rows={rows}
      showActions
      actionsLabel="Notes"
      renderActions={renderActions}
      sortColumnId={sortColumnId}
      sortDirection={sortDirection}
      onSort={handleSort}
      pagination={{
        currentPage,
        totalPages,
        pageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
      }}
    />
  )
}

export default PaymentHistoryTable
